<template>
    <section>
        <div class="text-center card-details--container py-5">
            <img class="card-details--image" src="@/assets/images/decoration/icons/Padlock.jpg" alt="secure payments image">
            <p class="card-details--text" v-text="$t('CARD_DETAILS_FORM_AND_LAST')"/>
        </div>

        <div v-if="isCanvasser" class="radio-container">
            <radio-field
                v-for="(item, index) in options"
                :key="index"
                v-model="val"
                :option="item.option"
                :label="item.label"/>
        </div>

        <div v-if="skipCard === false">
            <!-- Stripe Payment Request Button -->
            <stripe-payment-request-button v-if="isWalletEnabled && setupIntent" :setupIntent="setupIntent"/>

            <!-- QR Code -->
            <QRCode v-if="isCanvasser && val==='qrcode'"/>

            <!-- Stripe Terminal from -->
            <stripe-terminal-form v-if="showTerminal" @register="registerCanvassedCustomer"/>

            <!-- Stripe Payment Request Button -->
            <stripe-payment-request-button v-if="showPaymentRequestButton" :setupIntent="setupIntent" @register="registerCustomer"/>

            <!--Stripe Elements form-->
            <stripe-elements-form
                v-if="showStripeElementForm && setupIntent"
                ref="cardForm"
                :full-name="fullName"
                :billing-address="deliveryAddressIsBilling ? deliveryAddress : billingAddress"
                :has-been-requested="userHasRequesed"/>

            <!--Billing address form-->
            <div>
                <checkbox
                    :initialValue = "deliveryAddressIsBilling"
                    v-model="deliveryAddressIsBilling">
                    {{ $t('BILLING_ADDRESS_FORM_BILLING_ADDRESS') }}
                </checkbox>

                <div :class="billingAddressContainerClass" :style="billingAddressContainerStyle">
                    <billing-address-form
                        :error-handler="errorHandler"
                        v-model="billingAddress"/>
                </div>
            </div>
        </div>

        <div v-if="allowSkipCard">
            <checkbox
                v-model="skipCard">
                {{ $t('CARD_DETAILS_FORM_I_WILL_ENTER') }}
            </checkbox>
        </div>

        <!--Error list-->
        <div class="text-center">
            <p class="error" v-if="errorHandler.hasErrorMessage()" v-text="errorHandler.getErrorMessage()"></p>
            <p class="error my-2" v-text="error" v-if="error"></p>
        </div>
        <div class="w-full p-2" v-if="errorHandler.getAllErrors()">
            <ul class="error text-center">
                <li v-for="(errorMsg, key) in errorHandler.getAllErrors()" :key="key">
                    <span class="font-bold capitalize" v-text="key"/>:
                    <span class="font-normal" v-text="errorMsg"/>
                </li>
            </ul>
        </div>
        <supported-cards />
        <bumper-box-check-out v-if="showProductTypeSelect"/>
        <order-summary />
        <!-- Form button -->
        <div v-if="showSignUp" class="text-center">
            <submit-button
                :processing="processing"
                color="green"
                @click="submitCurrentStep()">
                {{ $t('CARD_DETAILS_FORM_SIGN_ME_UP') }}
            </submit-button>
        </div>
    </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { SECTION_PAYMENT } from '@/constants/Registration';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import Checkbox from '@/components/forms/fields/Checkbox';
import RegistrationService from '@/services/users/RegistrationService';
import SubmitButton from '@/components/forms/buttons/SubmitButton';
import BillingAddressForm from '@/components/forms/addresses/BillingAddressForm';
import StripeElementsForm from '@/components/forms/cardDetails/StripeElementsForm';
import StripePaymentRequestButton from '../cardDetails/StripePaymentRequestButton';
import OrderSummary from '@/components/forms/fields/OrderSummary';
import SupportedCards from '@/components/forms/fields/SupportedCards';
import BumperBoxCheckOut from '@/components/forms/fields/BumperBoxCheckOut';
import StripeTerminalForm from '../cardDetails/StripeTerminalForm';
import RadioField from '@/components/forms/fields/RadioField';
import QRCode from '@/components/structure/QRCode';

export default {
    name: 'CardDetailsForm',
    data() {
        return {
            processing: false,
            skipCard: false,
            showCouponCode: false,
            userHasRequesed: false,
            billingAddressOverflowVisible: false,
            errorHandler: new ErrorResponseHandler(),
            error: null,
            options: [
                {
                    option: 'card',
                    label: this.$t('CARD_OPTION'),
                },
                {
                    option: 'terminal',
                    label: this.$t('READER_OPTION'),
                },
                {
                    option: 'qrcode',
                    label: this.$t('QR_OPTION'),
                },
            ],
            val: 'card',
        };
    },
    props: {
        allowSkipCard: {
            default: false,
            type: Boolean,
        },
        enableCouponField: {
            default: false,
            type: Boolean,
        },
    },
    mounted() {
        this.fetchStripeSetupIntent({ email: this.email, gateway: 'Stripe' });
        this.showCouponCode = (this.friendCode || '').length > 0;
    },
    methods: {
        ...mapActions({
            fetchStripeSetupIntent: 'account/fetchStripeSetupIntent',
        }),
        // Handle validating and submitting this step.
        submitCurrentStep() {
            this.userHasRequesed = true;
            this.error = null;

            this.processing = true;

            if (this.skipCard === false) {
                if (this.is3DSEnabled) {
                    this.$refs.cardForm.handleScaCardSubmission()
                        .then(tokenData => {
                            this.isCanvasser
                                ? this.registerCanvassedCustomer(tokenData.setupIntentId, tokenData.setupIntentId)
                                : this.registerCustomer(tokenData.setupIntentId, tokenData.setupIntentId);
                        })
                        .catch((err) => {
                            this.error = err;
                            this.handleFail();
                        });
                } else {
                    this.$refs.cardForm.handleFetchingToken()
                        .then(tokenData => {
                            this.isCanvasser
                                ? this.registerCanvassedCustomer(tokenData.tokenId, tokenData.cardId)
                                : this.registerCustomer(tokenData.tokenId, tokenData.cardId);
                        })
                        .catch((err) => {
                            this.error = err;
                            this.handleFail();
                        });
                }
            } else {
                // Only canvasser can get here.
                this.registerCanvassedCustomer();
            }
        },
        // Continue to register the customer with our API.
        registerCustomer(tokenId, cardId, paymentMethod = 'card', nameOfCard) {
            RegistrationService.registerCustomer(tokenId, cardId, paymentMethod, nameOfCard)
                .then(() => {
                    this.$store.commit('register/setSectionComplete', SECTION_PAYMENT);
                    this.$emit('registered');
                })
                .catch(error => {
                    this.handleFail();
                    this.$store.commit('account/setSetupIntent', null);
                    const hasAmex = error.response?.data?.error.includes('Amex');
                    if (hasAmex) return this.errorHandler.setResponse({ error: this.$t('AMERICAN_EXPRESS_NOT_SUPPORTED') });
                    setTimeout(() => this.errorHandler.setResponse(error.response?.data), 0);
                });
        },
        // Continue to register the canvassed customer with our API.
        registerCanvassedCustomer(tokenId = null, cardId = null, customerId = null) {
            RegistrationService.registerCanvassedCustomer(tokenId, cardId, customerId)
                .then(() => {
                    this.$store.commit('register/setSectionComplete', SECTION_PAYMENT);
                    this.$store.commit('canvasser/resetCardDetails');
                    this.$emit('registered');
                })
                .catch(error => {
                    setTimeout(() => this.errorHandler.setResponse(error.response?.data), 0);
                    this.handleFail();
                });
        },
        // Handle failed sign up or card token request.
        handleFail() {
            this.processing = false;
            this.fetchStripeSetupIntent({ email: this.email, gateway: 'Stripe' });
            this.$scrollTo(`.input--invalid`, {
                offset: -100,
            });
        },
    },
    computed: {
        ...mapGetters({
            email: 'register/getEmail',
            fullName: 'register/getFullName',
            firstDeliveryDate: 'register/getFirstDeliveryDay',
            setupIntent: 'account/getSetupIntent',
            isCanvasser: 'auth/isCanvasser',
            is3DSEnabled: 'payment/is3DSEnabled',
        }),

        billingAddress: {
            get() {
                return this.$store.getters['register/getBillingAddress'];
            },
            set(value) {
                this.$store.commit('register/setBillingAddress', value);
            },
        },

        deliveryAddress: {
            get() {
                return this.$store.getters['register/getDeliveryAddress'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryAddress', value);
            },
        },

        friendCode: {
            get() {
                return this.$store.getters['register/getFriendCode'];
            },
            set(code) {
                this.$store.commit('register/setFriendCode', code);
            },
        },

        deliveryAddressIsBilling: {
            get() {
                return this.$store.getters['register/getDeliveryAddressIsBilling'];
            },
            set(value) {
                this.$store.commit('register/setDeliveryAddressIsBilling', value);
            },
        },

        billingAddressContainerClass() {
            return !this.deliveryAddressIsBilling ? 'accordion__md accordion__md--active' : 'accordion__md';
        },

        billingAddressContainerStyle() {
            return this.billingAddressOverflowVisible ? { overflow: 'visible' } : {};
        },
        showProductTypeSelect() {
            return this.$globalQueryParams?.bumper === 'true';
        },
        isWalletEnabled() {
            return process.env.VUE_APP_ENABLE_STRIPE_WALLET === 'true';
        },
        showTerminal() {
            return this.isCanvasser && this.val === 'terminal';
        },
        showSignUp() {
            return (this.isCanvasser && this.val === 'card') || !this.isCanvasser || this.skipCard;
        },
        showStripeElementForm() {
            return (this.isCanvasser && this.val === 'card') || !this.isCanvasser;
        },
        showPaymentRequestButton() {
            return this.setupIntent && !this.isCanvasser && this.$globalQueryParams.uuid;
        },
    },
    watch: {
        showCouponCode(value) {
            if (value === false) {
                this.friendCode = null;
            }
        },
        deliveryAddressIsBilling: {
            immediate: true,
            handler: function(value) {
                if (value === false) {
                    // Delay overflow visible while the expand transition is taking place
                    setTimeout(() => {
                        this.billingAddressOverflowVisible = true;
                    }, 500);
                } else {
                    this.billingAddressOverflowVisible = false;
                }
            },
        },
        val(value) {
            this.$emit('input', value);
        },
    },
    components: {
        Checkbox,
        SubmitButton,
        StripeElementsForm,
        StripePaymentRequestButton,
        BillingAddressForm,
        OrderSummary,
        BumperBoxCheckOut,
        SupportedCards,
        StripeTerminalForm,
        RadioField,
        QRCode,
    },
};
</script>

<style scoped>
    .radio-container{
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-around;
        margin: 20px 20px;
    }
</style>
